import { forwardRef, type InputHTMLAttributes } from 'react';

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: {
    radio: 'w-4 h-4',
    dot: 'w-1.5 h-1.5',
    label: 'text-sm',
  },
  md: {
    radio: 'w-5 h-5',
    dot: 'w-2 h-2',
    label: 'text-base',
  },
  lg: {
    radio: 'w-6 h-6',
    dot: 'w-2.5 h-2.5',
    label: 'text-lg',
  },
};

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      disabled,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;
    const styles = sizeStyles[size];

    return (
      <div className={`relative flex items-start ${className}`}>
        <div className="flex items-center h-6">
          <div className="relative">
            <input
              ref={ref}
              id={radioId}
              type="radio"
              disabled={disabled}
              className="sr-only peer"
              {...props}
            />
            <div
              className={`
                ${styles.radio}
                rounded-full border-2 transition-all duration-150
                flex items-center justify-center
                bg-white dark:bg-surface-800
                peer-focus-visible:ring-2 peer-focus-visible:ring-accent-500 peer-focus-visible:ring-offset-2
                peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-offset-surface-900
                peer-disabled:opacity-50 peer-disabled:cursor-not-allowed
                ${error
                  ? 'border-danger-500'
                  : 'border-surface-300 dark:border-surface-600 peer-checked:border-accent-500'
                }
              `}
            >
              <div
                className={`
                  ${styles.dot}
                  rounded-full bg-accent-500
                  opacity-0 peer-checked:opacity-100
                  scale-0 peer-checked:scale-100
                  transition-all duration-150
                `}
              />
            </div>
          </div>
        </div>

        {(label || description) && (
          <label
            htmlFor={radioId}
            className={`ml-3 cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {label && (
              <span className={`block font-medium text-surface-900 dark:text-surface-100 ${styles.label}`}>
                {label}
              </span>
            )}
            {description && (
              <span className="block text-sm text-surface-500 dark:text-surface-400 mt-0.5">
                {description}
              </span>
            )}
          </label>
        )}

        {error && (
          <p className="absolute -bottom-5 left-0 text-sm text-danger-600 dark:text-danger-400">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

// RadioGroup for managing multiple radios
interface RadioGroupProps {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  options: Array<{
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }>;
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  error?: string;
  className?: string;
}

export function RadioGroup({
  name,
  value,
  onChange,
  options,
  orientation = 'vertical',
  size = 'md',
  error,
  className = '',
}: RadioGroupProps) {
  return (
    <div
      className={`
        flex gap-4
        ${orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'}
        ${className}
      `}
      role="radiogroup"
    >
      {options.map((option) => (
        <Radio
          key={option.value}
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange?.(option.value)}
          label={option.label}
          description={option.description}
          disabled={option.disabled}
          size={size}
        />
      ))}
      {error && (
        <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
      )}
    </div>
  );
}

export default Radio;
